import { Classes, TextArea } from "@blueprintjs/core"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import { EMPTY_OBJECT_ID, DATE_TIME_FORMAT } from "modules/common/constants"
import { MessageType } from "modules/orders-manage/models/order-question"
import moment from "moment"
import React, { useCallback, useRef, useState } from "react"
import styles from "./QuestionComments.module.scss"

const initials = (name: string) => {
  const parts = name.split(" ").filter((p) => !!p)

  if (!parts.length) {
    return "?"
  }

  return `${parts[0][0] || ""}${parts.length > 1 ? parts[1][0] : ""}`.toUpperCase()
}

/** Лента комментариев задания */
export const QuestionComments: React.FC<QuestionCommentsProps> = ({
  messages,
  currentUserId,
  onAdd,
  onEdit,
  onRemove,
}) => {
  const [text, setText] = useState("")
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState("")
  const [busy, setBusy] = useState(false)

  const listRef = useRef<HTMLDivElement>(null)

  // в колонке прокручивается только лента, поэтому колесо в любом её месте крутим сами:
  // над полем ввода и отступами оно упиралось в неподвижную колонку, а над самой лентой
  // не доходило до неё
  const wheel = useCallback((e: React.WheelEvent) => {
    const list = listRef.current
    const target = e.target as HTMLElement

    // в поле ввода прокручивается сам текст
    if (!list || (target.closest && target.closest("textarea"))) {
      return
    }

    const was = list.scrollTop

    list.scrollTop = was + e.deltaY

    if (list.scrollTop !== was) {
      e.preventDefault()
    }
  }, [])

  const add = useCallback(async () => {
    const value = text.trim()

    if (!value) {
      return
    }

    setBusy(true)
    await onAdd({
      id: EMPTY_OBJECT_ID,
      description: value,
      employer: null,
      day: "",
      hours: "",
      date: moment(new Date()).format(DATE_TIME_FORMAT),
    })
    setBusy(false)
    setText("")
  }, [text, onAdd])

  const saveEdit = useCallback(
    async (message: MessageType) => {
      const value = editText.trim()

      if (!value) {
        return
      }

      setBusy(true)
      await onEdit({ ...message, description: value })
      setBusy(false)
      setEditingId(null)
      setEditText("")
    },
    [editText, onEdit]
  )

  return (
    <div className={styles.comments} onWheel={wheel}>
      <div className={styles.editor}>
        <TextArea
          value={text}
          className={`${Classes.FILL} ${styles.input}`}
          growVertically={true}
          placeholder="Написать комментарий..."
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault()
              add()
            }
          }}
        />
        <div className={styles.editorButtons}>
          <PlanrButton type="lightblueish" size="small" onClick={add} disabled={busy || !text.trim()}>
            Отправить
          </PlanrButton>
          <span className={styles.hint}>Ctrl + Enter</span>
        </div>
      </div>

      <div className={styles.list} ref={listRef}>
        {messages.map((message, index) => {
          const own = !message.employer || message.employer.id === currentUserId
          const name = message.employer ? message.employer.name : "Вы"
          const editing = editingId === (message.id || `${index}`)

          return (
            <div className={styles.item} key={message.id || index}>
              <div className={`${styles.avatar} ${own ? styles.ownAvatar : ""}`}>{initials(name)}</div>

              <div className={styles.content}>
                <div className={styles.meta}>
                  <span className={styles.author}>{name}</span>
                  <span className={styles.date}>{!message.day ? message.date : `${message.day} ${message.hours}`}</span>

                  {own && !editing && (
                    <span className={styles.actions}>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(message.id || `${index}`)
                          setEditText(message.description)
                        }}
                      >
                        Изменить
                      </button>
                      <button type="button" onClick={() => onRemove(message, index)}>
                        Удалить
                      </button>
                    </span>
                  )}
                </div>

                {editing ? (
                  <div className={styles.editRow}>
                    <TextArea
                      value={editText}
                      autoFocus={true}
                      growVertically={true}
                      className={Classes.FILL}
                      onChange={(e) => setEditText(e.target.value)}
                    />
                    <div className={styles.editButtons}>
                      <PlanrButton type="greenish" size="small" onClick={() => saveEdit(message)} disabled={busy}>
                        Сохранить
                      </PlanrButton>
                      <PlanrButton
                        type="graybtn"
                        size="small"
                        onClick={() => {
                          setEditingId(null)
                          setEditText("")
                        }}
                      >
                        Отмена
                      </PlanrButton>
                    </div>
                  </div>
                ) : (
                  <div className={styles.text}>{message.description}</div>
                )}
              </div>
            </div>
          )
        })}

        {messages.length === 0 && <div className={styles.empty}>Комментариев пока нет</div>}
      </div>
    </div>
  )
}

interface QuestionCommentsProps {
  messages: MessageType[]
  currentUserId: string
  onAdd: (message: MessageType) => Promise<any>
  onEdit: (message: MessageType) => Promise<any>
  onRemove: (message: MessageType, index: number) => void
}
